import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Avatar, Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import femaleAvatar1 from '../../../asset/female_avatar_1.png';
import femaleAvatar2 from '../../../asset/female_avatar_2.png';
import maleAvatar from '../../../asset/male_avatar.png';
import AudioPlayButton from '../../student/components/AudioPlayButton';
import { BLUE, blueButtonSx, cardSx, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT } from '../../student/studentStyles';

const teacherOptions = [
    { value: 'male', src: maleAvatar, label: 'Male teacher voice', caption: 'Male' },
    { value: 'female', src: femaleAvatar1, label: 'Female teacher voice', caption: 'Female A' },
    { value: 'female2', src: femaleAvatar2, label: 'Second female teacher voice', caption: 'Female B' },
] as const;

const audioSpeedOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'fast', label: 'Fast' },
] as const;

const readingOptions = [
    { value: 'characters', label: 'Characters', hint: '汉字 + pinyin' },
    { value: 'pinyin', label: 'Pinyin only', hint: 'no 汉字' },
] as const;

const scoringOptions = [
    { value: 'easy', label: 'Not Bad' },
    { value: 'medium', label: 'Strong' },
    { value: 'hard', label: 'Pro' },
] as const;

export interface StudentDesktopPreferences {
    teacher: 'male' | 'female' | 'female2';
    audioSpeed: 'normal' | 'fast';
    reading: 'characters' | 'pinyin';
    scoring: 'easy' | 'medium' | 'hard';
}

function OptionButton({ label, hint, selected, onClick }: { label: string; hint?: string; selected: boolean; onClick: () => void }) {
    return (
        <ButtonBase
            onClick={onClick}
            aria-pressed={selected}
            sx={{
                flex: 1, minWidth: 120, minHeight: 70, px: 2, py: 1.25, borderRadius: '14px', flexDirection: 'column',
                border: `2px solid ${selected ? BLUE : RING_TRACK}`, backgroundColor: selected ? `${BLUE}0C` : '#fff',
                transition: 'border-color 150ms ease, background-color 150ms ease',
            }}
        >
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13, color: selected ? BLUE : NAVY }}>{label}</Typography>
            {hint && <Typography sx={{ mt: 0.3, fontFamily: INTER, fontSize: 11, color: SLATE }}>{hint}</Typography>}
        </ButtonBase>
    );
}

function SectionHeading({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '13px', display: 'grid', placeItems: 'center', bgcolor: `${BLUE}0E`, color: BLUE }}>{icon}</Box>
            <Box>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>{title}</Typography>
                <Typography sx={{ mt: 0.25, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>{description}</Typography>
            </Box>
        </Stack>
    );
}

export default function StudentDesktopSettingsPage({ preferences, onSave }: { preferences: StudentDesktopPreferences; onSave: (preferences: StudentDesktopPreferences) => void }) {
    const [teacher, setTeacher] = React.useState<StudentDesktopPreferences['teacher']>(preferences.teacher);
    const [audioSpeed, setAudioSpeed] = React.useState<StudentDesktopPreferences['audioSpeed']>(preferences.audioSpeed);
    const [reading, setReading] = React.useState<StudentDesktopPreferences['reading']>(preferences.reading);
    const [scoring, setScoring] = React.useState<StudentDesktopPreferences['scoring']>(preferences.scoring);

    return (
        <Box sx={{ maxWidth: 1080, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
            <Box sx={{ mb: 3 }}>
                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, md: 30 }, color: NAVY }}>Settings</Typography>
                <Typography sx={{ mt: 0.4, fontFamily: INTER, fontSize: 14, color: SLATE }}>Choose how lessons sound, display, and score your practice.</Typography>
            </Box>

            <Stack spacing={2.5}>
                <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
                    <SectionHeading icon={<RecordVoiceOverRoundedIcon />} title="Teacher voice" description="Choose the voice you hear during lessons and examples." />
                    <Stack direction="row" spacing={{ xs: 2, sm: 4 }} justifyContent="center" sx={{ mt: 3 }}>
                        {teacherOptions.map(option => {
                            const selected = teacher === option.value;
                            return (
                                <ButtonBase key={option.value} onClick={() => setTeacher(option.value)} aria-label={option.label} aria-pressed={selected} sx={{ p: 1, borderRadius: '16px', flexDirection: 'column' }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Avatar src={option.src} alt={option.label} sx={{ width: { xs: 62, sm: 72 }, height: { xs: 62, sm: 72 }, border: `3px solid ${selected ? BLUE : 'transparent'}` }} />
                                        {selected && <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 23, height: 23, borderRadius: '50%', bgcolor: BLUE, color: '#fff', display: 'grid', placeItems: 'center', border: '2px solid #fff' }}><CheckRoundedIcon sx={{ fontSize: 15 }} /></Box>}
                                    </Box>
                                    <Typography sx={{ mt: 0.75, fontFamily: INTER, fontWeight: selected ? 700 : 500, fontSize: 12, color: selected ? BLUE : SLATE }}>{option.caption}</Typography>
                                </ButtonBase>
                            );
                        })}
                        <Box sx={{ alignSelf: 'center', ml: { xs: '0 !important', sm: '12px !important' } }}><AudioPlayButton ariaLabel="Preview selected teacher voice" size={40} /></Box>
                    </Stack>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>
                    <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
                        <SectionHeading icon={<GraphicEqRoundedIcon />} title="Audio speed" description="Set the default playback speed for lesson audio." />
                        <Stack direction="row" spacing={1.25} sx={{ mt: 3 }}>
                            {audioSpeedOptions.map(option => <OptionButton key={option.value} {...option} selected={audioSpeed === option.value} onClick={() => setAudioSpeed(option.value)} />)}
                        </Stack>
                    </Box>

                    <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
                        <SectionHeading icon={<TranslateRoundedIcon />} title="Reading display" description="Choose what appears alongside spoken Chinese." />
                        <Stack direction="row" spacing={1.25} sx={{ mt: 3 }}>
                            {readingOptions.map(option => <OptionButton key={option.value} {...option} selected={reading === option.value} onClick={() => setReading(option.value)} />)}
                        </Stack>
                    </Box>
                </Box>

                <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
                    <SectionHeading icon={<TuneRoundedIcon />} title="Pronunciation grading" description="Adjust how strict the speaking feedback is. You can change this anytime." />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 3 }}>
                        {scoringOptions.map(option => <OptionButton key={option.value} {...option} selected={scoring === option.value} onClick={() => setScoring(option.value)} />)}
                    </Stack>
                </Box>

                <Box sx={{ position: 'sticky', bottom: 0, py: 2, px: { xs: 0, sm: 2 }, display: 'flex', justifyContent: 'flex-end', background: `linear-gradient(transparent, ${SURFACE_SOFT} 28%)` }}>
                    <Button variant="contained" onClick={() => onSave({ teacher, audioSpeed, reading, scoring })} sx={{ ...blueButtonSx, minWidth: 160, px: 3 }}>Save settings</Button>
                </Box>
            </Stack>
        </Box>
    );
}
